// Typed client for the authenticated bridge passthrough
// (functions/api/bridge/[action].js -> src/V6Api.js). Every call here is
// same-origin, sends the session cookie automatically, and never touches
// an Apps Script token directly — the browser never sees one.

import { apiRequest } from './apiClient';

export interface FieldJob {
  jobId: string;
  customerId?: string;
  customerName?: string;
  address?: string;
  status?: string;
  currentTotal?: number;
  [key: string]: unknown;
}

export interface FieldAssignedJobsResponse {
  ok: boolean;
  jobs?: FieldJob[];
  jobSchedules?: Record<string, unknown>;
  message?: string;
}

export function fetchAssignedJobs() {
  return apiRequest<FieldAssignedJobsResponse>('/bridge/fieldAssignedJobs', { method: 'POST', body: {} });
}

export interface RosterWorker {
  workerId: string;
  name: string;
  phone: string;
  defaultDayRate: number;
  active: boolean;
  notes: string;
}

export function fetchWorkerRoster() {
  return apiRequest<{ ok: boolean; workers?: RosterWorker[]; message?: string }>('/bridge/workerRosterList', {
    method: 'POST',
    body: {},
  });
}

export function upsertWorkerRoster(worker: Partial<RosterWorker>) {
  return apiRequest<{ ok: boolean; worker?: RosterWorker; message?: string }>('/bridge/workerRosterUpsert', {
    method: 'POST',
    body: worker,
  });
}

export interface JobFieldLeadAssignments {
  jobId: string;
  primary: string;
  additional: string[];
}

export function fetchJobFieldLeadAssignments(jobId: string) {
  return apiRequest<{ ok: boolean; assignments?: JobFieldLeadAssignments; message?: string }>('/bridge/jobFieldLeadAssignments', {
    method: 'POST',
    body: { jobId },
  });
}

export function setPrimaryFieldLead(jobId: string, username: string) {
  return apiRequest<{ ok: boolean; assignments?: JobFieldLeadAssignments; message?: string }>('/bridge/jobFieldLeadSetPrimary', {
    method: 'POST',
    body: { jobId, username },
  });
}

export function addAdditionalFieldLead(jobId: string, username: string) {
  return apiRequest<{ ok: boolean; assignments?: JobFieldLeadAssignments; message?: string }>('/bridge/jobFieldLeadAddAdditional', {
    method: 'POST',
    body: { jobId, username },
  });
}

export function removeFieldLeadAssignment(jobId: string, username: string) {
  return apiRequest<{ ok: boolean; assignments?: JobFieldLeadAssignments; message?: string }>('/bridge/jobFieldLeadRemove', {
    method: 'POST',
    body: { jobId, username },
  });
}

// --- Prepare Job / Build Today's Crew / Start-Pause-Resume-Complete Work
// (v6 architecture doc §9.3). Every mutation returns the same shape as
// jobDetail's `operations` (or the worker-job bundle `appWorkerJobData_`
// returns server-side) so the screen can refresh in one round trip instead
// of a second fetch — mirrors the existing HTML UI's pattern.

export interface ChecklistItem {
  checklistId: string;
  lineNo: number;
  description: string;
  details?: string;
  qty: number;
  unit: string;
  status: string;
  reason?: string;
  photoCount: number;
  [key: string]: unknown;
}

export interface WorkSession {
  sessionId: string;
  status: string;
  startedAt: string;
  startedAtMs: number;
  crewNames: string;
  durationMinutes: number;
  active: boolean;
  [key: string]: unknown;
}

export interface JobOperations {
  jobId: string;
  documentNo: string;
  schedule: Record<string, unknown>[];
  expenses: Record<string, unknown>[];
  notes: Record<string, unknown>[];
  photos: Record<string, unknown>[];
  workSessions: WorkSession[];
  checklist: ChecklistItem[];
  checklistProgress: { complete: number; total: number; text: string };
  workSummary: { sessionCount: number; elapsedMinutes: number; elapsedText: string; laborMinutes: number; laborText: string; workerHours: Record<string, unknown>[] };
}

export interface JobDetailResponse {
  ok: boolean;
  job?: Record<string, unknown> & { jobId: string; documentNo?: string; customer?: string; project?: string; address?: string };
  documents?: Record<string, unknown>[];
  commercial?: Record<string, unknown>;
  operations?: JobOperations;
  message?: string;
}

export function fetchJobDetail(jobId: string) {
  return apiRequest<JobDetailResponse>('/bridge/jobDetail', { method: 'POST', body: { jobId } });
}

export interface WorkerJobResponse {
  ok: boolean;
  message?: string;
  [key: string]: unknown;
}

export function startWorkSession(input: { jobId: string; crewSize: number; workerNames: string[]; notes?: string }) {
  return apiRequest<WorkerJobResponse>('/bridge/startWorkSession', { method: 'POST', body: input });
}

export function pauseWorkSession(input: { jobId: string; notes?: string; continuationDate?: string; continuationStartTime?: string; continuationEndTime?: string; continuationNotes?: string }) {
  return apiRequest<WorkerJobResponse>('/bridge/pauseWorkSession', { method: 'POST', body: input });
}

export function completeWorkSession(input: { jobId: string; notes?: string }) {
  return apiRequest<WorkerJobResponse>('/bridge/completeWorkSession', { method: 'POST', body: input });
}

export function saveChecklistItem(input: { jobId: string; checklistId: string; status: string; reason?: string }) {
  return apiRequest<WorkerJobResponse>('/bridge/saveChecklistItem', { method: 'POST', body: input });
}

export function addCrewNote(input: { jobId: string; note: string; noteType?: string; visibleToCrew?: boolean; pinned?: boolean }) {
  return apiRequest<{ ok: boolean; noteId?: string; operations?: JobOperations; message?: string }>('/bridge/addCrewNote', {
    method: 'POST',
    body: input,
  });
}

export function uploadJobPhoto(input: { jobId: string; dataUrl: string; category?: string; caption?: string; checklistId?: string; fileName?: string }) {
  return apiRequest<{ ok: boolean; photoId?: string; checklistId?: string; operations?: JobOperations; message?: string }>('/bridge/uploadJobPhoto', {
    method: 'POST',
    body: input,
  });
}

// --- Stage 2: Dashboard, Customers, Jobs directories ---

export interface DashboardMetrics {
  activeJobs: number;
  waitingEstimates: number;
  unpaidInvoices: number;
  outstandingText: string;
  collectedThisMonthText: string;
}

export interface DashboardResponse {
  ok: boolean;
  message?: string;
  dashboard?: { metrics: DashboardMetrics; recentJobs: JobSummary[]; recentEstimates: JobSummary[] };
  roleDashboard?: { kind: string; title: string; assignedJobs?: number } & Record<string, unknown>;
  pendingApprovalsCount?: number;
}

export function fetchDashboard() {
  return apiRequest<DashboardResponse>('/bridge/dashboardBootstrap', { method: 'POST', body: {} });
}

export interface JobSummary {
  jobId: string;
  documentNo: string;
  customerId?: string;
  customer?: string;
  project?: string;
  address?: string;
  stage?: string;
  estimateStatus?: string;
  folderStatus?: string;
  total?: number;
  totalText?: string;
  amountPaid?: number;
  amountPaidText?: string;
  balance?: number;
  balanceText?: string;
  [key: string]: unknown;
}

export function fetchJobsList(query = '', filter = '') {
  return apiRequest<{ ok: boolean; jobs?: JobSummary[]; message?: string }>('/bridge/jobsList', {
    method: 'POST',
    body: { query, filter },
  });
}

export interface CustomerSummary {
  customerId: string;
  name: string;
  phone?: string;
  email?: string;
  address?: string;
  jobCount?: number;
  [key: string]: unknown;
}

export function fetchCustomersList(query = '') {
  return apiRequest<{ ok: boolean; customers?: CustomerSummary[]; message?: string }>('/bridge/customersList', {
    method: 'POST',
    body: { query },
  });
}

export function fetchEstimatesList(query = '') {
  return fetchJobsList(query, 'estimate');
}

// --- Stage 3 (read-only slice): Price Book. Full estimate creation/editing
// is a deliberately separate, dedicated piece of work — see the comment in
// src/V6Api.js's `priceBookList` action.

export interface PriceBookItem {
  serviceId: string;
  category: string;
  name: string;
  description?: string;
  unit: string;
  currentPrice: number;
  currentPriceText: string;
  minimumChargeText?: string;
  active: boolean;
  [key: string]: unknown;
}

export interface PriceBookAddOn {
  addOnId: string;
  category: string;
  name: string;
  unit: string;
  currentPriceText: string;
  active: boolean;
  [key: string]: unknown;
}

export function fetchPriceBook() {
  return apiRequest<{ ok: boolean; canEdit?: boolean; categories?: string[]; items?: PriceBookItem[]; addOns?: PriceBookAddOn[]; message?: string }>(
    '/bridge/priceBookList',
    { method: 'POST', body: {} },
  );
}

// --- Stage 5: Schedule ---

export interface ScheduleEntry {
  scheduleId: string;
  jobId: string;
  documentNo: string;
  customer: string;
  project: string;
  serviceDate: string;
  serviceDateKey: string;
  startTime: string;
  endTime: string;
  assignedCrew: string;
  status: string;
  address: string;
  [key: string]: unknown;
}

export function fetchScheduleWindow(startOffset = 0, endOffset = 13) {
  return apiRequest<{ ok: boolean; schedule?: ScheduleEntry[]; message?: string }>('/bridge/scheduleWindow', {
    method: 'POST',
    body: { startOffset, endOffset },
  });
}

// --- Stage 6 (read-only slice): Team, Activity Log, Performance. See the
// comment on the `teamList` action in src/V6Api.js for why account/role/
// permission writes are not included here.

export interface TeamMember {
  username: string;
  name: string;
  role: string;
  roleLabel: string;
  status: string;
  lastLogin?: string;
  locked?: boolean;
  isCurrent?: boolean;
  [key: string]: unknown;
}

export interface TeamListResponse {
  ok: boolean;
  message?: string;
  users?: TeamMember[];
  currentUser?: { username: string; role: string };
  roles?: { value: string; label: string }[];
  permissionCatalog?: { key: string; label: string }[];
  rolePresets?: Record<string, Record<string, boolean>>;
}

export function fetchTeamList() {
  return apiRequest<TeamListResponse>('/bridge/teamList', { method: 'POST', body: {} });
}

export interface SaveTeamMemberInput {
  username: string;
  originalUsername?: string;
  name: string;
  email?: string;
  role: string;
  status?: 'Active' | 'Inactive';
  notes?: string;
  permissions?: Record<string, boolean>;
}

export interface SaveTeamMemberResponse extends TeamListResponse {
  created?: boolean;
  username?: string;
  name?: string;
  temporaryPassword?: string;
  mustChangePassword?: boolean;
}

export function saveTeamMember(input: SaveTeamMemberInput) {
  return apiRequest<SaveTeamMemberResponse>('/bridge/teamSaveUser', { method: 'POST', body: input });
}

export function setTeamMemberStatus(username: string, status: 'Active' | 'Inactive') {
  return apiRequest<TeamListResponse>('/bridge/teamSetUserStatus', { method: 'POST', body: { username, status } });
}

export interface ActivityLogItem {
  timestamp: string;
  name: string;
  action: string;
  entityType: string;
  entityId: string;
  details: string;
}

export function fetchActivityLog(limit = 30) {
  return apiRequest<{ ok: boolean; items?: ActivityLogItem[]; message?: string }>('/bridge/activityLog', {
    method: 'POST',
    body: { limit },
  });
}

export interface PerformanceEmployee {
  name: string;
  username: string;
  role: string;
  upsellsText: string;
  bonusEarnedText: string;
  referrals: number;
  reviews: number;
  [key: string]: unknown;
}

export interface IncentiveRecord {
  incentiveId: string;
  employeeName: string;
  employeeUsername: string;
  type: string;
  amount: number;
  status: string;
  [key: string]: unknown;
}

export interface AdminAlertRecord {
  alertId: string;
  title: string;
  detail: string;
  severity: string;
  status: string;
  createdAt?: string;
  [key: string]: unknown;
}

export interface PerformanceCenterResponse {
  ok: boolean;
  message?: string;
  role?: string;
  scope?: string;
  summary?: { upsellsText: string; discountsText: string; referrals: number; reviews: number } & Record<string, unknown>;
  employees?: PerformanceEmployee[];
  myIncentives?: Record<string, unknown>[];
  myReferrals?: Record<string, unknown>[];
  allIncentives?: IncentiveRecord[];
  alerts?: AdminAlertRecord[];
  pendingReviews?: PendingReviewRecord[];
}

export interface PendingReviewRecord {
  reviewId: string;
  jobId: string;
  fieldLead: string;
  platform: string;
  reviewerName?: string;
  reviewUrl?: string;
  proofUrl?: string;
  submittedAt?: string;
}

export function fetchPerformanceCenter() {
  return apiRequest<PerformanceCenterResponse>('/bridge/performanceCenter', { method: 'POST', body: {} });
}

// --- Billing writes. See the comment on `billingCreateInvoice` in
// src/V6Api.js for exactly what stays enforced server-side (work-complete
// gating, balance checks, the document lock, the billingWrite role gate).

export interface BillingWorkflowResponse {
  ok: boolean;
  message?: string;
  record?: JobSummary;
  paymentId?: string;
  invoicePdf?: string;
}

export function createInvoice(documentNo: string) {
  return apiRequest<BillingWorkflowResponse>('/bridge/billingCreateInvoice', { method: 'POST', body: { documentNo } });
}

export function recordPayment(input: { documentNo: string; amount: number; method?: string; reference?: string; notes?: string; paymentDate?: string }) {
  return apiRequest<BillingWorkflowResponse>('/bridge/billingRecordPayment', { method: 'POST', body: input });
}

export interface PaymentRecord {
  paymentId: string;
  documentNo: string;
  date: string;
  amount: number;
  amountText: string;
  method: string;
  reference?: string;
  recordedBy?: string;
}

export function fetchPaymentHistory(documentNo: string) {
  return apiRequest<{ ok: boolean; payments?: PaymentRecord[]; message?: string }>('/bridge/billingPaymentHistory', {
    method: 'POST',
    body: { documentNo },
  });
}

// --- Contract writes. See the comment on `contractCreate` in src/V6Api.js
// for what stays enforced server-side (licensing/insurance readiness,
// internal-test acknowledgment, book-first gate, role gate).

export interface ContractSetupInfo {
  internalTest: boolean;
  customerReady: boolean;
  operationalMissing: string[];
  licensingMissing: string[];
  message: string;
}

export function fetchContractSetupInfo() {
  return apiRequest<{ ok: boolean; setup?: ContractSetupInfo; message?: string }>('/bridge/contractSetupInfo', { method: 'POST', body: {} });
}

export interface CreateContractInput {
  documentNo: string;
  startDate: string;
  completionDate: string;
  paymentPlan: string;
  customTerms?: string;
  materials: string;
  customerItems?: string;
  cleanup?: string;
  materialSpecs?: string;
  unforeseen?: string;
  specialOrder?: string;
  internalTestAcknowledged?: boolean;
}

export interface ContractWorkflowResponse extends BillingWorkflowResponse {
  signUrl?: string;
  contractSetup?: ContractSetupInfo;
}

export function createContract(input: CreateContractInput) {
  return apiRequest<ContractWorkflowResponse>('/bridge/contractCreate', { method: 'POST', body: input });
}

export function waiveContract(documentNo: string) {
  return apiRequest<BillingWorkflowResponse>('/bridge/contractWaive', { method: 'POST', body: { documentNo } });
}

// --- Full/Quick Estimate builder. See the comment on `estimateWorkspace`
// in src/V6Api.js for exactly what stays enforced/unenforced server-side
// (no margin-floor check at this stage — that lives in the Change Order
// path; the frozen-once-booked/contract-signed/payment-exists edit locks;
// the Field/Job Lead restriction).

export interface EstimateLineItem {
  serviceId?: string;
  category: string;
  description: string;
  details?: string;
  qty: number;
  unit: string;
  rate: number;
  priceSource?: string;
  measurementType?: string;
  measurementData?: Record<string, unknown>;
}

export interface EstimateDraftSummary {
  draftId: string;
  estimateType: string;
  status: string;
  customerName: string;
  project: string;
  updatedAt?: string;
  totalText?: string;
  [key: string]: unknown;
}

export interface EstimateWorkspaceResponse {
  ok: boolean;
  message?: string;
  canEditPrices?: boolean;
  priceBook?: PriceBookItem[];
  addOns?: PriceBookAddOn[];
  categories?: string[];
  drafts?: EstimateDraftSummary[];
}

export function fetchEstimateWorkspace() {
  return apiRequest<EstimateWorkspaceResponse>('/bridge/estimateWorkspace', { method: 'POST', body: {} });
}

export interface EstimateCustomerMatch {
  customerId: string;
  name: string;
  phone?: string;
  email?: string;
  address?: string;
}

export function searchEstimateCustomers(query: string, limit = 10) {
  return apiRequest<{ ok: boolean; customers?: EstimateCustomerMatch[] }>('/bridge/estimateSearchCustomers', {
    method: 'POST',
    body: { query, limit },
  });
}

export interface EstimateFormState {
  draftId?: string;
  documentNo?: string;
  referralId?: string;
  estimateType?: string;
  customerId?: string;
  customerName: string;
  phone?: string;
  email?: string;
  billingAddress?: string;
  serviceAddress?: string;
  project: string;
  leadSource?: string;
  customerNotes?: string;
  paymentTerms?: string;
  discount?: number;
  taxRate?: number;
  items: EstimateLineItem[];
}

export function checkCustomerDuplicate(input: Partial<EstimateFormState>) {
  return apiRequest<{ ok: boolean; match: { customerId: string; name: string; reasons: string[] } | null }>('/bridge/estimateCheckDuplicate', {
    method: 'POST',
    body: input,
  });
}

export function saveEstimateDraft(input: EstimateFormState) {
  return apiRequest<{ ok: boolean; draftId?: string; total?: number; totalText?: string; message?: string }>('/bridge/estimateSaveDraft', {
    method: 'POST',
    body: input,
  });
}

export function loadEstimateDraft(draftId: string) {
  return apiRequest<{ ok: boolean; draft?: EstimateFormState; message?: string }>('/bridge/estimateLoadDraft', {
    method: 'POST',
    body: { draftId },
  });
}

export function deleteEstimateDraft(draftId: string) {
  return apiRequest<{ ok: boolean; message?: string }>('/bridge/estimateDeleteDraft', { method: 'POST', body: { draftId } });
}

export interface EstimateSaveResponse {
  ok: boolean;
  message?: string;
  documentNo?: string;
  jobId?: string;
  customerId?: string;
  pdfUrl?: string;
  total?: number;
  totalText?: string;
}

export function createEstimate(input: EstimateFormState) {
  return apiRequest<EstimateSaveResponse>('/bridge/estimateCreate', { method: 'POST', body: input });
}

export function fetchEstimateForEdit(documentNo: string) {
  return apiRequest<{ ok: boolean; estimate?: EstimateFormState & { jobId?: string; customerId?: string; estimateStatus?: string; currentPdf?: string }; message?: string }>(
    '/bridge/estimateGetForEdit',
    { method: 'POST', body: { documentNo } },
  );
}

export function updateEstimate(input: EstimateFormState) {
  return apiRequest<EstimateSaveResponse>('/bridge/estimateUpdate', { method: 'POST', body: input });
}

// --- Change Orders (field upsells/reductions) — the app's actual
// margin-floor enforcement layer. See the comment on `changeOrderCatalog`
// in src/V6Api.js for the full list of what stays enforced server-side.

export interface UpsellCatalogItem {
  serviceId: string;
  category: string;
  name: string;
  description?: string;
  unit: string;
  currentPrice: number;
  currentPriceText: string;
  defaultQty: number;
  maxFieldDiscountPct: number;
  safeDiscountPct: number;
}

export function fetchChangeOrderCatalog(jobId: string, query = '') {
  return apiRequest<{ ok: boolean; blockedCount?: number; message?: string; items?: UpsellCatalogItem[] }>('/bridge/changeOrderCatalog', {
    method: 'POST',
    body: { jobId, query },
  });
}

export interface ChangeOrderItemInput {
  serviceId: string;
  qty: number;
  discountPct?: number;
  details?: string;
}

export interface ChangeOrder {
  changeOrderId: string;
  jobId: string;
  documentNo: string;
  changeType: string;
  status: string;
  netChange: number;
  netChangeText: string;
  revisedTotal: number;
  revisedTotalText: string;
  reason?: string;
  createdBy: string;
  createdAt: string;
  approvalUrl?: string;
  unsignedPdf?: string;
  signedPdf?: string;
  [key: string]: unknown;
}

export interface ChangeOrderResponse {
  ok: boolean;
  message?: string;
  changeOrder?: ChangeOrder;
  commercial?: Record<string, unknown>;
  adminAlertId?: string;
}

export function createChangeOrder(input: { jobId: string; changeType: string; reason?: string; items: ChangeOrderItemInput[] }) {
  return apiRequest<ChangeOrderResponse>('/bridge/changeOrderCreate', { method: 'POST', body: input });
}

export function submitCustomPriceRequest(input: { jobId: string; description: string; measurements?: string; materials?: string; notes?: string; reason?: string }) {
  return apiRequest<ChangeOrderResponse>('/bridge/changeOrderSubmitCustomPrice', { method: 'POST', body: input });
}

export function approveChangeOrder(input: { changeOrderId: string; approvedPrice?: number }) {
  return apiRequest<ChangeOrderResponse>('/bridge/changeOrderApprove', { method: 'POST', body: input });
}

export function rejectChangeOrder(input: { changeOrderId: string; reason?: string }) {
  return apiRequest<ChangeOrderResponse>('/bridge/changeOrderReject', { method: 'POST', body: input });
}

// --- Concurrent-edit locks ---

export interface EditLockResult {
  ok: boolean;
  owned?: boolean;
  locked?: boolean;
  holderName?: string;
  expiresAt?: string;
  message?: string;
}

export function acquireEditLock(entityType: string, entityId: string, sessionId: string) {
  return apiRequest<EditLockResult>('/bridge/editLockAcquire', { method: 'POST', body: { entityType, entityId, sessionId } });
}

export function releaseEditLock(entityType: string, entityId: string, sessionId: string) {
  return apiRequest<{ ok: boolean }>('/bridge/editLockRelease', { method: 'POST', body: { entityType, entityId, sessionId } });
}

export function heartbeatEditLock(entityType: string, entityId: string, sessionId: string) {
  return apiRequest<EditLockResult>('/bridge/editLockHeartbeat', { method: 'POST', body: { entityType, entityId, sessionId } });
}

// --- Team account recovery ---

export function unlockTeamMember(username: string) {
  return apiRequest<TeamListResponse>('/bridge/teamUnlockAccount', { method: 'POST', body: { username } });
}

export function resetTeamMemberPassword(username: string) {
  return apiRequest<SaveTeamMemberResponse>('/bridge/teamResetPassword', { method: 'POST', body: { username } });
}

// --- Worker time-correction approvals (Owner/Admin) ---

export interface TimeApprovalItem {
  entryId: string;
  jobId: string;
  documentNo?: string;
  workerName: string;
  currentEndTime?: string;
  requestedEndTime?: string;
  correctionReason?: string;
  requestedBy?: string;
  requestedAt?: string;
  customer?: string;
  project?: string;
}

export function fetchPendingTimeApprovals() {
  return apiRequest<{ ok: boolean; items?: TimeApprovalItem[]; message?: string }>('/bridge/pendingTimeApprovals', { method: 'POST', body: {} });
}

export function approveTimeCorrection(entryId: string, adminNote?: string) {
  return apiRequest<{ ok: boolean; message?: string }>('/bridge/approveTimeCorrection', { method: 'POST', body: { entryId, adminNote } });
}

export function rejectTimeCorrection(entryId: string, adminNote?: string) {
  return apiRequest<{ ok: boolean; message?: string }>('/bridge/rejectTimeCorrection', { method: 'POST', body: { entryId, adminNote } });
}

// --- Incentive payouts and admin alerts (Owner/Admin financials) ---

export function markIncentivePaid(incentiveId: string) {
  return apiRequest<{ ok: boolean; incentiveId?: string; status?: string; message?: string }>('/bridge/incentiveMarkPaid', { method: 'POST', body: { incentiveId } });
}

export function markAdminAlertReviewed(alertId: string, status?: string, note?: string) {
  return apiRequest<{ ok: boolean; message?: string }>('/bridge/adminAlertMarkReviewed', { method: 'POST', body: { alertId, status, note } });
}

// --- Price Book editing (Owner/Admin/canEditPrices) ---

export function savePriceBookItem(input: Partial<PriceBookItem> & { serviceId?: string }) {
  return apiRequest<{ ok: boolean; message?: string }>('/bridge/priceBookSaveItem', { method: 'POST', body: input });
}

// --- Global search and Documents directory ---

export interface GlobalSearchResult {
  type: 'customer' | 'job';
  id: string;
  title: string;
  subtitle?: string;
  meta?: string;
}

export function fetchGlobalSearch(query: string) {
  return apiRequest<{ ok: boolean; results?: GlobalSearchResult[]; message?: string }>('/bridge/globalSearch', {
    method: 'POST',
    body: { query },
  });
}

export interface DocumentRecord {
  type: string;
  name: string;
  url: string;
  documentNo: string;
  jobId: string;
  customer: string;
  project: string;
  date?: string;
}

export function fetchDocumentsList(query = '') {
  return apiRequest<{ ok: boolean; documents?: DocumentRecord[]; message?: string }>('/bridge/documentsList', {
    method: 'POST',
    body: { query },
  });
}

// --- Owner-only Profit Dashboard ---

export interface ProfitWeek {
  weekStart: string;
  weekEnd: string;
  grossText: string;
  jobExpensesText: string;
  companyExpensesText: string;
  netText: string;
  companyShareText: string;
  samShareText: string;
  shakibShareText: string;
}

export interface ProfitPaidJob {
  jobId: string;
  completionDate: string;
  customerJob: string;
  revenueText: string;
  expensesText: string;
  directProfitText: string;
  marginText: string;
}

export function fetchProfitDashboard() {
  return apiRequest<{
    ok: boolean;
    message?: string;
    currentWeek?: ProfitWeek;
    ytd?: ProfitWeek;
    recentWeeks?: ProfitWeek[];
    recentPaidJobs?: ProfitPaidJob[];
  }>('/bridge/profitDashboard', { method: 'POST', body: {} });
}

// --- Customer Presentations. See the comment on `presentationWorkspace`
// in src/V6Api.js for the full unmodified-function list this wraps.

export interface PresentationSession {
  presentationId: string;
  customerName: string;
  project: string;
  status: string;
  presentationTitle: string;
  selectedTotalText: string;
  updatedAt: string;
  [key: string]: unknown;
}

export interface ProductCatalogItem {
  productId: string;
  category: string;
  productType: string;
  brand: string;
  productName: string;
  description?: string;
  customerPrice: number;
  installServiceId?: string;
  imageUrl?: string;
  active: boolean;
  [key: string]: unknown;
}

export interface PresentationWorkspaceResponse {
  ok: boolean;
  message?: string;
  sessions?: PresentationSession[];
  products?: ProductCatalogItem[];
  priceBook?: PriceBookItem[];
}

export function fetchPresentationWorkspace() {
  return apiRequest<PresentationWorkspaceResponse>('/bridge/presentationWorkspace', { method: 'POST', body: {} });
}

export interface PresentationOption {
  optionId?: string;
  packageName?: string;
  category?: string;
  type?: string;
  serviceId?: string;
  productId?: string;
  title: string;
  description?: string;
  qty: number;
  unit?: string;
  unitPrice: number;
  amount?: number;
  selected: boolean;
  sortOrder?: number;
  imageUrl?: string;
  notes?: string;
  measurementRef?: string;
}

export interface PresentationMeasurement {
  measurementId?: string;
  name: string;
  length: number;
  width: number;
  height: number;
  doors?: number;
  windows?: number;
  closets?: number;
  notes?: string;
}

export interface PresentationDetail extends PresentationSession {
  draftId?: string;
  customerId?: string;
  notes?: string;
  serviceAddress?: string;
  phone?: string;
  email?: string;
  billingAddress?: string;
  leadSource?: string;
  activePackage?: string;
  options: PresentationOption[];
  measurements: PresentationMeasurement[];
}

export function fetchPresentation(presentationId: string) {
  return apiRequest<{ ok: boolean; presentation?: PresentationDetail; message?: string }>('/bridge/presentationLoad', {
    method: 'POST',
    body: { presentationId },
  });
}

export interface SavePresentationInput {
  presentationId?: string;
  draftId?: string;
  customerId?: string;
  customerName: string;
  project: string;
  status?: string;
  activePackage?: string;
  notes?: string;
  presentationTitle?: string;
  serviceAddress?: string;
  jobId?: string;
  phone?: string;
  email?: string;
  billingAddress?: string;
  leadSource?: string;
  options: PresentationOption[];
  measurements: PresentationMeasurement[];
}

export function savePresentation(input: SavePresentationInput) {
  return apiRequest<{ ok: boolean; presentation?: PresentationDetail; message?: string }>('/bridge/presentationSave', {
    method: 'POST',
    body: input,
  });
}

export function deletePresentation(presentationId: string) {
  return apiRequest<{ ok: boolean; sessions?: PresentationSession[]; message?: string }>('/bridge/presentationDelete', {
    method: 'POST',
    body: { presentationId },
  });
}

export function convertPresentationToEstimate(presentationId: string) {
  return apiRequest<{ ok: boolean; draftId?: string; total?: number; totalText?: string; message?: string }>('/bridge/presentationToEstimate', {
    method: 'POST',
    body: { presentationId },
  });
}

export function saveProductCatalogItem(input: Partial<ProductCatalogItem>) {
  return apiRequest<{ ok: boolean; item?: ProductCatalogItem; message?: string }>('/bridge/productCatalogSave', {
    method: 'POST',
    body: input,
  });
}

export function setProductStatus(productId: string, active: boolean) {
  return apiRequest<{ ok: boolean; products?: ProductCatalogItem[]; message?: string }>('/bridge/productSetStatus', {
    method: 'POST',
    body: { productId, active },
  });
}

// --- Review-request and referral submission (Field Lead), review
// verification (Manager) — docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md
// §9.3's "review-request prompt, referral submission".

export function submitReview(input: { jobId: string; platform?: string; reviewerName?: string; reviewUrl?: string; proofDataUrl?: string }) {
  return apiRequest<{ ok: boolean; reviewId?: string; status?: string; message?: string }>('/bridge/reviewSubmit', {
    method: 'POST',
    body: input,
  });
}

export function verifyReview(reviewId: string) {
  return apiRequest<{ ok: boolean; incentiveId?: string; message?: string }>('/bridge/reviewVerify', { method: 'POST', body: { reviewId } });
}

export function rejectReview(reviewId: string, reason?: string) {
  return apiRequest<{ ok: boolean; message?: string }>('/bridge/reviewReject', { method: 'POST', body: { reviewId, reason } });
}

export function submitReferral(input: { sourceJobId?: string; customerName: string; phone: string; email?: string; address?: string; requestedWork: string; notes?: string; permissionToContact: boolean }) {
  return apiRequest<{ ok: boolean; referral?: Record<string, unknown>; message?: string }>('/bridge/referralSubmit', {
    method: 'POST',
    body: input,
  });
}

export function fetchCustomerDetail(customerId: string) {
  return apiRequest<{
    ok: boolean;
    customer?: CustomerSummary;
    records?: JobSummary[];
    summary?: { jobCount: number; totalQuotedText: string; totalPaidText: string };
    message?: string;
  }>('/bridge/customerDetail', { method: 'POST', body: { customerId } });
}

export function recordJobExpense(input: {
  jobId: string;
  category: string;
  amount: number;
  vendor?: string;
  description?: string;
  paymentMethod?: string;
  notes?: string;
  receiptDataUrl?: string;
  receiptUrl?: string;
}) {
  return apiRequest<{ ok: boolean; expenseId?: string; operations?: JobOperations; message?: string }>('/bridge/recordJobExpense', {
    method: 'POST',
    body: input,
  });
}
